import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiMode';
import { Upload, Trash2, FileText, Image, FolderOpen } from 'lucide-react';

const tdS = { padding: '0.7rem 0.9rem', borderBottom: '1px solid #f1f5f9', fontSize: '0.865rem', verticalAlign: 'middle' };
const thS = { ...tdS, background: '#f8fafc', fontWeight: 700, fontSize: '0.73rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' };

const MAX_FILE_MB = 15;

export default function AdminContentSection({ token }) {
  const api = useMemo(() => axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/api/partner/content');
      setFiles(r.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Errore caricamento file');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); }, [token]);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    const fileMB = file.size / 1024 / 1024;
    if (fileMB > MAX_FILE_MB) {
      setUploadError(`File troppo grande (max ${MAX_FILE_MB} MB). Questo file è ${fileMB.toFixed(1)} MB.`);
      return;
    }

    const isPDF = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');
    if (!isPDF && !isImage) {
      setUploadError('Formato non supportato. Carica solo PDF o immagini (JPG, PNG, GIF, WebP).');
      return;
    }

    setUploadError('');
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(',')[1];
        await api.post('/api/partner/admin/content', {
          name: file.name,
          file_type: isPDF ? 'pdf' : 'image',
          mime_type: file.type,
          file_data: base64
        });
        await load();
        setUploading(false);
      };
      reader.onerror = () => {
        setUploadError('Errore lettura file.');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Errore upload');
      setUploading(false);
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Eliminare "${file.name}"?`)) return;
    try {
      await api.delete(`/api/partner/admin/content/${file.id}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Errore eliminazione');
    }
  };

  const FileIcon = ({ type }) => type === 'image'
    ? <Image size={18} color="#7c3aed" />
    : <FileText size={18} color="#1d4ed8" />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e3a8a', fontWeight: 800 }}>Materiali Commerciali</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.87rem' }}>
            Carica PDF e immagini che gli utenti partner possono scaricare. Max {MAX_FILE_MB} MB per file.
          </p>
        </div>
        <div>
          <input
            type="file"
            accept=".pdf,image/*"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 8, padding: '0.55rem 1rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.87rem', opacity: uploading ? 0.7 : 1 }}
          >
            <Upload size={15} /> {uploading ? 'Caricamento...' : 'Carica File'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.75rem', borderRadius: 8, marginBottom: '1rem', border: '1px solid #fecaca' }}>{error}</div>
      )}
      {uploadError && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.75rem', borderRadius: 8, marginBottom: '1rem', border: '1px solid #fecaca' }}>
          {uploadError}
          <button onClick={() => setUploadError('')} style={{ marginLeft: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', fontWeight: 700 }}>✕</button>
        </div>
      )}

      {loading ? (
        <div style={{ color: '#64748b' }}>Caricamento...</div>
      ) : files.length === 0 ? (
        <div style={{ background: 'white', border: '2px dashed #cbd5e1', borderRadius: 12, padding: '3rem', textAlign: 'center', color: '#64748b' }}>
          <FolderOpen size={40} style={{ opacity: 0.3, marginBottom: '1rem' }} />
          <p style={{ marginBottom: '1rem' }}>Nessun file caricato.</p>
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, padding: '0.55rem 1rem', fontWeight: 700, cursor: 'pointer' }}
          >
            <Upload size={15} /> Carica il primo file
          </button>
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>{['Tipo', 'Nome File', 'Caricato il', 'Azioni'].map(h => <th key={h} style={thS}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {files.map(file => (
                <tr key={file.id}>
                  <td style={{ ...tdS, width: 60 }}>
                    <FileIcon type={file.file_type} />
                  </td>
                  <td style={{ ...tdS, fontWeight: 600, color: '#0f172a' }}>
                    {file.name}
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#64748b', fontWeight: 400 }}>
                      ({file.file_type?.toUpperCase()})
                    </span>
                  </td>
                  <td style={tdS}>{fmt(file.created_at)}</td>
                  <td style={tdS}>
                    <button
                      onClick={() => handleDelete(file)}
                      style={{ border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', borderRadius: 7, padding: '0.3rem 0.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', fontWeight: 700, fontSize: '0.78rem' }}
                    >
                      <Trash2 size={13} /> Elimina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
